import * as React from 'react'
import classnames from 'classnames'
import Image from 'next/image'
import { useDropzone } from 'react-dropzone'
import { withFormik, Form, Field, FormikProps, ErrorMessage } from 'formik'
import * as Yup from 'yup'

import { Checkbox } from '../elements/Checkbox'
import { Input } from '../elements/Input'
import { Label } from '../elements/Label'
import { Textarea } from '../elements/Textarea'
import styles from './styles.module.scss'
import { ErrorText } from '../elements/ErrorText'
import TermsOfServiceText from '../../components/TermsOfServiceText'

export interface FormValues {
  content: string
  email: string
  has_candle: boolean
  image?: File
  location: string
  name: string
}

interface Props {
  canAddCandle: boolean
  deceasedFirstName: string
  initialValues: FormValues
  onSubmit: (values: FormValues) => Promise<void>
}

const validationSchema = Yup.object({
  content: Yup.string().when('has_candle', {
    is: false,
    then: Yup.string().trim().required('A candle or message is required'),
  }),
  email: Yup.string().trim().email().required('Required'),
  name: Yup.string().trim().required('Required'),
})

const ObituaryMemoryForm: React.FC<Props & FormikProps<FormValues>> = (props) => {
  const { setFieldValue, setFieldTouched, values } = props
  const onDrop = React.useCallback(
    (acceptedFiles) => {
      props.setFieldValue('image', acceptedFiles[0])
      props.setFieldTouched('image')
    },
    [setFieldTouched, setFieldValue],
  )
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })
  const imageWrapperClass = classnames(styles.imageWrapper, {
    [styles.imageWrapperDragging]: isDragActive,
  })

  return (
    <Form className={styles.form}>
      <div className={styles.top}>
        <div className={styles.messageWrapper}>
          <Label htmlFor="content">Message (Optional)</Label>

          <Field
            as={Textarea}
            className={styles.messageContent}
            name="content"
            placeholder={`Share condolences, tell a story, or just simply express what ${props.deceasedFirstName} meant to you.`}
          />

          <ErrorMessage component={ErrorText} name="content" />
        </div>

        <div {...getRootProps()} className={imageWrapperClass}>
          <input {...getInputProps()} />
          <Image alt="camera" src="/icon-camera.png" height={20} width={24} />
          <span>{values.image ? values.image.name : 'Upload a photo'}</span>
        </div>
      </div>

      <div className={styles.middle}>
        <div>
          <Label htmlFor="name">Your name</Label>

          <Field as={Input} name="name" required={true} />

          <ErrorMessage component={ErrorText} name="name" />
        </div>

        <div>
          <Label htmlFor="email">Email</Label>

          <Field as={Input} name="email" required={true} type="email" />

          <ErrorMessage component={ErrorText} name="email" />
        </div>

        <div>
          <Label htmlFor="location">Location (Optional)</Label>

          <Field as={Input} name="location" />
        </div>
      </div>

      <div className={styles.bottom}>
        {props.canAddCandle && (
          <div className={styles.checkboxesWrapper}>
            <Field as={Checkbox} labelText="Light a candle" name="has_candle" />
          </div>
        )}

        <div>
          <TermsOfServiceText />
        </div>
      </div>

      <div className={styles.submitBtnWrapper}>
        <button className={styles.button} disabled={props.isSubmitting} type="submit">
          Share
        </button>
      </div>
    </Form>
  )
}

export default withFormik<Props, FormValues>({
  displayName: 'ObituaryMemoryForm',
  enableReinitialize: true,
  handleSubmit: async (values, { props, setFieldTouched, setFieldValue }) => {
    await props.onSubmit(values)

    setFieldTouched('content', false)
    setFieldTouched('image', false)

    setFieldValue('content', '')
    setFieldValue('image', undefined)
  },
  mapPropsToValues: (props) => props.initialValues,
  validateOnMount: true,
  validationSchema,
})(ObituaryMemoryForm)
